import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function MoviePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const movie = movies.find((m) => m.id === movieId);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">影片未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === movie.category);
  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `《${movie.title}》可以在悟空影视免费观看吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的！《${movie.title}》已在悟空影视上线，高清画质免费在线观看，无需注册会员，点击即可播放。`,
        },
      },
      {
        '@type': 'Question',
        name: `《${movie.title}》的画质怎么样？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '悟空影视采用先进的视频编码技术，支持1080P高清画质播放，为您呈现影院级的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.name || movie.category, href: `/category/${movie.category}` },
                  { label: movie.title },
                ]}
              />
            </div>

            <article className="flex flex-col lg:flex-row gap-8 md:gap-10 mb-12">
              <div className="flex-shrink-0 w-full sm:w-[300px] mx-auto lg:mx-0">
                <div className="rounded-xl overflow-hidden border border-background-300/40">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full aspect-[2/3] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {movie.isHot && (
                    <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                      热播
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                      新片
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full bg-background-200/70 text-foreground-600 text-xs border border-background-300/40">
                    {category?.name || movie.category}
                  </span>
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-black text-foreground-950 mb-2">
                  {movie.title}
                </h1>

                <div className="flex items-center gap-4 mb-6 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-primary-500"></i>
                    <span className="text-lg font-bold text-foreground-950">{movie.rating}</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="text-sm text-foreground-600">{movie.year}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="text-sm text-foreground-600">{movie.duration}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="text-sm text-foreground-600">{movie.views}次播放</span>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-500 whitespace-nowrap mt-0.5">导演：</span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground-500 whitespace-nowrap mt-0.5">主演：</span>
                    <span className="text-sm text-foreground-800">{movie.cast.join(' / ')}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-heading font-bold text-foreground-950 mb-3">
                    剧情简介
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {movie.description}
                  </p>
                </div>

                <Link
                  to={`/movie/${movie.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-xl"></i>
                  在线观看
                </Link>
              </div>
            </article>

            {relatedMovies.length > 0 && (
              <section className="border-t border-background-300/40 pt-10 md:pt-12">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}